/**
 * Auth store — manages user session, profile, and login state.
 */
import { create } from "zustand";
import type { UserPreferences, UserProfile } from "../transport/authClient";
import {
  ApiRequestError,
  fetchMe,
  login as apiLogin,
  logout as apiLogout,
  updatePreferences as apiUpdatePrefs,
} from "../transport/authClient";

interface AuthState {
  user: UserProfile | null;
  loading: boolean;
  error: string | null;
  initialized: boolean;

  initialize: () => Promise<void>;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  updatePreferences: (prefs: Partial<UserPreferences>) => Promise<void>;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  loading: false,
  error: null,
  initialized: false,

  initialize: async () => {
    if (get().initialized) return;
    set({ loading: true, error: null });
    try {
      const user = await fetchMe();
      set({ user, loading: false, initialized: true });
    } catch (e) {
      // Açılışta 401 bir ARIZA DEĞİLDİR — henüz giriş yapılmamış demektir.
      // Bunu hata olarak göstermek, siteye ilk gelen herkese kırmızı bir
      // rozet gösteriyordu. Durum kodu artık hatanın üzerinde taşındığı
      // için mesaj metninde alt dize aramaya gerek yok.
      const isAuthError = e instanceof ApiRequestError && e.status === 401;
      set({
        user: null,
        loading: false,
        initialized: true,
        error: isAuthError
          ? null
          : e instanceof Error
            ? e.message
            : "Bağlantı hatası",
      });
    }
  },

  login: async (email, password) => {
    set({ loading: true, error: null });
    try {
      const user = await apiLogin({ email, password });
      set({ user, loading: false, error: null });
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Giriş başarısız";
      set({ loading: false, error: msg });
      throw e;
    }
  },

  logout: async () => {
    try {
      await apiLogout();
    } finally {
      set({ user: null, error: null });
    }
  },

  updatePreferences: async (prefs) => {
    const { user } = get();
    if (!user) throw new Error("Oturum bulunamadı");
    try {
      const result = await apiUpdatePrefs(prefs);
      set({
        user: { ...user, preferences: result.preferences },
      });
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Tercihler kaydedilemedi";
      set({ error: msg });
      throw e;
    }
  },
}));
